import React, { useState, useRef, useEffect } from 'react';
import {
  Edit2, Calendar, CheckCircle2, MoreVertical, UserPlus,
  X, Trash2, Check, Search, ChevronLeft, ChevronRight,
  LayoutGrid, Clock, FolderOpen, ChevronDown,
  ShieldCheck,
  Briefcase
} from 'lucide-react';
import ReusableTable from '../components/ReusableTable';
import { apiUrl } from '../ApiHelper/ApiUrl';
import { handleFetchRequest } from '../ApiHelper/ApiHelper';
import { getInitials } from '../common/helpers';
import { AVAILABLE_USERS, availableLanguages, dummyFrameworks, dummyProjects, mockUsers } from '../dummy/dummy';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { createProject, geProjectLists } from '../redux/features/project/projectSlice';

const Projects = () => {
  // --- States ---
  const [projects, setProjects] = useState(dummyProjects ?? []);

  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);


  const [projectName, setProjectName] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [frameworks, setFrameworks] = useState([]);
  const [selectedFrameworks, setSelectedFrameworks] = useState([]);

  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [tempSelectedLanguages, setTempSelectedLanguages] = useState([]);

  const [selectedAuditor, setSelectedAuditor] = useState(null);
  const [selectedCoordinator, setSelectedCoordinator] = useState(null);

  const [openDropdown, setOpenDropdown] = useState(null);
  const [tempSelectedFrameworks, setTempSelectedFrameworks] = useState([]);
  
  const [pageIsLoading, setPageIsLoading] = useState(false);

  const dispatch = useDispatch();



  const fallbackFrameworks = dummyFrameworks ?? [];
  const getFrameworkList = () => {
    setPageIsLoading(true);
    handleFetchRequest("framework", `${apiUrl.framework_list}`).then((response) => {
        if (response?.statusCode === 200) {
            setFrameworks(response?.data ?? fallbackFrameworks);
        } else {
            setFrameworks(fallbackFrameworks);
        }
        setPageIsLoading(false);
    });
  };
  
  const getProjectsListHandler = async () => {
    setPageIsLoading(true);
    try {
      const result = await dispatch(geProjectLists()).unwrap();
      if(Array.isArray(result?.data)) {
        // console.log("##projects", result?.data);
        // setProjects(result?.data);
      }

    } catch (error) {
      console.error("Failed to fetch projects:", error);
      // ❌ Show user-friendly error
      message.error(
        typeof error === "string"
          ? error
          : error?.message || "Failed to load projects"
      );
    } finally {
      setPageIsLoading(false);
    }
  };

  useEffect(() => {
    getFrameworkList();
    getProjectsListHandler();
  }, []);

  // --- Pagination States ---
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  const dropdownRef = useRef(null);
  // --- Dedicated Refs ---
  const modalBodyRef = useRef(null);
  const auditorRef = useRef(null);
  const coordinatorRef = useRef(null);
  const languageRef = useRef(null);
  const frameworkRef = useRef(null);
  const teamDropdownRef = useRef(null);

  const initialFormState = {
    name: '',
    description: '',
    projectId: '',
    status: 'Active',
    assignedUsers: [],
    auditor_id: null,
    coordinator_id: null,
    dueDate: '',
    languages: [],
    frameworks: [],
  };
  const [formData, setFormData] = useState(initialFormState);
  const [editingProjectId, setEditingProjectId] = useState(null);

  // --- Pagination Calculations ---
  const totalPages = Math.ceil(projects.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProjects = projects.slice(indexOfFirstItem, indexOfLastItem);

  // --- Effects ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // useEffect(() => {
  //   if (isUserDropdownOpen && modalBodyRef.current) {
  //     setTimeout(() => {
  //       modalBodyRef.current.scrollTo({
  //         top: modalBodyRef.current.scrollHeight,
  //         behavior: 'smooth'
  //       });
  //     }, 50);
  //   }
  // }, [isUserDropdownOpen]);
    useEffect(() => {
      const activeState = openDropdown || (isUserDropdownOpen ? 'team' : null);
      
      if (activeState && modalBodyRef.current) {
        setTimeout(() => {
          let activeElement = null;
          
          if (activeState === 'auditor') activeElement = auditorRef.current;
          else if (activeState === 'coordinator') activeElement = coordinatorRef.current;
          else if (activeState === 'language') activeElement = languageRef.current;
          else if (activeState === 'framework') activeElement = frameworkRef.current;
          else if (activeState === 'team') activeElement = teamDropdownRef.current;
  
          if (activeElement) {
            // Scroll the specific element into the center of the modal view smoothly
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 100); // 100ms timeout ensures the DOM has painted the dropdown menu
      }
    }, [isUserDropdownOpen, openDropdown]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUserToggle = (userName) => {
    setFormData((prev) => {
      const isSelected = prev.assignedUsers.includes(userName);
      return {
        ...prev,
        assignedUsers: isSelected
          ? prev.assignedUsers.filter(name => name !== userName)
          : [...prev.assignedUsers, userName]
      };
    });
  };

  const handleEditClick = (project) => {
    setEditingProjectId(project.id);
    const mappedUsers = project.users.map(u => {
      return AVAILABLE_USERS.find(name => getInitials(name) === u.initials) || null;
    }).filter(Boolean);

    setFormData({
      name: project.name,
      description: project.description,
      projectId: project.id,
      status: project.status,
      assignedUsers: mappedUsers,
      auditor: project.auditor || null,
      coordinator: project.coordinator || null,
      dueDate: project.dueDate || '',
      languages: project.languages || [],
      frameworks: project.frameworks || []
    });
    setIsModalOpen(true);
    setOpenDropdownId(null);
  };

  const handleSaveProject1 = () => {
    if (!formData.name || !formData.description || !formData.projectId || formData.assignedUsers.length === 0) {
      alert("Please fill out all required fields.");
      return;
    }

    const formattedUsers = formData.assignedUsers.map((name, index) => ({
      initials: getInitials(name),
      theme: index % 2 === 0 ? 'dark' : 'light'
    }));

    if (editingProjectId) {
      setProjects(projects.map(p => p.id === editingProjectId ? {
        ...p,
        name: formData.name,
        description: formData.description,
        status: formData.status,
        users: formattedUsers
      } : p));
    } else {
      if (projects.some(p => p.id === formData.projectId)) {
        alert("Project ID already exists.");
        return;
      }

      const now = new Date();
      const newProject = {
        id: formData.projectId,
        name: formData.name,
        description: formData.description,
        users: formattedUsers,
        creator: 'Current User',
        creatorInitials: 'CU',
        date: now.toISOString().split('T')[0],
        time: now.toTimeString().split(' ')[0].substring(0, 5),
        status: formData.status
      };
      setProjects([newProject, ...projects]);
      setCurrentPage(1);
    }
    closeModal();
  };

  const handleSaveProject = async () => {    
    if (!formData.name || !formData.description || !formData.projectId || formData.assignedUsers.length === 0) {
      alert("Please fill out all required fields.");
      return;
    }
    try {
      console.log("##formdata", formData);
      const payload = {
        orgId: process.env.REACT_APP_ORG_ID,
        name: formData?.name,
        description: formData?.description,        
        project_alias_id: formData?.projectId,
        status: formData?.status,
        auditor_id: formData?.auditor?.id || "02a2cd62-0224-423c-889d-c107fd31d2c2",
        coordinator_id: formData?.coordinator?.id || "02a2cd62-0224-423c-889d-c107fd31d2c2",
        due_date: formData?.dueDate,
        language: formData?.languages || [],
        frameworkIds: formData?.frameworks || [],
        assigned_user_ids: [],
      }

      const result = await dispatch(createProject(payload)).unwrap();
      if(result?.status) {
        console.log("##project craeted", result?.data);        
        closeModal();
      }
    } catch (error) {
      console.log("##error", error);
    }
  }

  const handleDeleteProject = (id) => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      setProjects(projects.filter(p => p.id !== id));
      setOpenDropdownId(null);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProjectId(null);
    setFormData(initialFormState);
    setIsUserDropdownOpen(false);
  };

  const handleChangeStatus = (projectId) => {
    const statusCycle = ['Active', 'Inactive', 'Completed'];
    setProjects(projects.map(project => {
      if (project.id === projectId) {
        const currentIndex = statusCycle.indexOf(project.status);
        const nextIndex = (currentIndex + 1) % statusCycle.length;
        return { ...project, status: statusCycle[nextIndex] };
      }
      return project;
    }));
    setOpenDropdownId(null);
  };

  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setOpenDropdownId(null);
    }
  };

  const columns = [
    {
      key: 'id',
      title: 'Project ID',
      headerClassName: 'pl-6 w-[100px]',
      cellClassName: 'pl-6',
      render: (project) => (
        <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-widest rounded-md ring-1 ring-slate-200/60">
          {project.id}
        </span>
      ),
    },
    {
      key: 'details',
      title: 'Project Details',
      render: (project) => (
        <div className="flex flex-col">
          <span className="text-sm font-extrabold text-slate-900 mb-0.5">{project.name}</span>
          <span className="text-xs font-medium text-slate-500 max-w-[250px] truncate">{project.description}</span>
        </div>
      ),
    },
    {
      key: 'team',
      title: 'Assigned Team',
      render: (project) => (
        <div className="flex items-center -space-x-2.5">
          {project.users.map((user, idx) => (
            <div
              key={idx}
              title={user.initials}
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-black ring-2 ring-white shadow-sm ${
                user.theme === 'light' ? 'bg-indigo-100 text-indigo-700' : 'bg-gradient-to-br from-indigo-500 to-blue-600 text-white'
              }`}
            >
              {user.initials}
            </div>
          ))}
        </div>
      ),
    },
    {
      key: 'creator',
      title: 'Created By',
      render: (project) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-600 ring-1 ring-slate-200 flex items-center justify-center text-xs font-bold shadow-sm">
            {project.creatorInitials}
          </div>
          <span className="text-sm font-bold text-slate-700">{project.creator}</span>
        </div>
      ),
    },
    {
      key: 'timeline',
      title: 'Timeline',
      render: (project) => (
        <div className="flex items-start gap-2.5">
          <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
          <div className="flex flex-col text-sm">
            <span className="font-bold text-slate-800">{project.date}</span>
            <span className="text-xs font-medium text-slate-400 flex items-center gap-1 mt-0.5">
              <Clock size={10} /> {project.time}
            </span>
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (project) => (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-black uppercase tracking-wider ring-1 shadow-sm ${
            project.status === 'Active' ? 'bg-emerald-50 text-emerald-700 ring-emerald-200/50' :
            project.status === 'Completed' ? 'bg-blue-50 text-blue-700 ring-blue-200/50' : 
            'bg-slate-50 text-slate-700 ring-slate-200/50'
          }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${
              project.status === 'Active' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' :
              project.status === 'Completed' ? 'bg-blue-500' : 'bg-slate-400'
            }`}></span>
          {project.status}
        </span>
      ),
    },
    {
      key: 'actions',
      title: 'Actions',
      headerClassName: 'pr-6 text-right',
      cellClassName: 'pr-6 text-right relative',
      render: (project) => (
        <>
          <button
            onClick={() => setOpenDropdownId(openDropdownId === project.id ? null : project.id)}
            className="p-2 bg-white ring-1 ring-transparent hover:ring-slate-200 rounded-xl text-slate-400 hover:text-slate-900 transition-all"
          >
            <MoreVertical className="w-5 h-5" />
          </button>
          
          {openDropdownId === project.id && (
            <div className="absolute right-6 bottom-0 sm:top-full mt-2 bg-white/95 backdrop-blur-xl ring-1 ring-slate-200/60 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-30 py-2 w-48 text-left animate-in fade-in slide-in-from-top-2" style={{height:"max-content"}}>
              <button onClick={() => handleEditClick(project)} className="w-full px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 flex items-center gap-3 transition-colors">
                <Edit2 size={16} /> Edit Project
              </button>
              <button onClick={() => handleChangeStatus(project.id)} className="w-full px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 hover:text-indigo-600 flex items-center gap-3 transition-colors">
                <CheckCircle2 size={16} /> Change Status
              </button>
              <div className="h-px bg-slate-100 my-1 mx-2" />
              <button onClick={() => handleDeleteProject(project.id)} className="w-full px-4 py-2.5 text-sm font-bold text-rose-600 hover:bg-rose-50 flex items-center gap-3 transition-colors">
                <Trash2 size={16} /> Delete Project
              </button>
            </div>
          )}
        </>
      ),
    }
  ];

  const handleApplyFrameworks = (e) => {
        e.preventDefault();
        setSelectedFrameworks(tempSelectedFrameworks);
        setFormData(prev => ({
          ...prev,
          frameworks: tempSelectedFrameworks   // ✅ save to formData
        }));
        setOpenDropdown(null);
    };

    const handleApplyLanguages = (e) => {
        e.preventDefault();
        setSelectedLanguages(tempSelectedLanguages);
        setFormData(prev => ({
          ...prev,
          languages: tempSelectedLanguages   // ✅ save to formData
        }));
        setOpenDropdown(null);
    };

  return (
    <div className="bg-slate-50  relative">
        
        {/* --- HERO HEADER --- */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
            <div className="flex items-center gap-4">
                <div className="p-3 bg-indigo-600 rounded-2xl shadow-lg shadow-indigo-600/20 text-white hidden sm:block">
                    <LayoutGrid size={28} strokeWidth={2} />
                </div>
                <div>
                    <h3 className="text-2xl font-black text-slate-900 tracking-tight">Projects</h3>
                    <p className="text-sm font-semibold text-slate-500 mt-0.5">Manage and track your team's compliance projects.</p>
                </div>
            </div>
            <button
                onClick={() => {
                setFormData(initialFormState);
                setEditingProjectId(null);
                setIsModalOpen(true);
                }}
                className="flex gap-2 items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-md active:scale-95"
            >
                <UserPlus size={18}  /> New Project
            </button>
        </div>

        {/* --- MAIN TABLE CARD --- */}
          <ReusableTable
            columns={columns} 
            data={currentProjects} 
            pagination={{
                currentPage,
                totalPages,
                onPageChange: paginate,
                indexOfFirstItem,
                indexOfLastItem,
                totalItems: projects.length
            }}
        />

      {/* --- MODAL OVERLAY --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity" onClick={closeModal}></div>
          <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl relative z-10 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 max-h-[90vh]">
            
            <div className="px-8 py-6 border-b border-slate-100 bg-white flex justify-between items-center z-20">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-50 text-indigo-600 rounded-xl">
                        {editingProjectId ? <Edit2 size={20} /> : <FolderOpen size={20} />}
                    </div>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight">
                        {editingProjectId ? 'Edit Project' : 'Create New Project'}
                    </h2>
                </div>
              <button onClick={closeModal} className="p-2 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Note the added pb-32 class here to give dropdowns breathing room */}
            <div ref={modalBodyRef} className="p-8 pb-32 space-y-6 overflow-y-auto bg-slate-50/30">
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Project Name <span className="text-rose-500">*</span></label>
                <input
                  type="text" name="name" value={formData.name} onChange={handleInputChange}
                  placeholder="e.g. Global Server Migration"
                  className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all placeholder-slate-400"
                />
              </div>

              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Description <span className="text-rose-500">*</span></label>
                <textarea
                  name="description" value={formData.description} onChange={handleInputChange}
                  placeholder="Briefly describe the project goals and scope..." rows="3"
                  className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all resize-none placeholder-slate-400"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Project ID <span className="text-rose-500">*</span></label>
                  <input
                    type="text" name="projectId" value={formData.projectId} onChange={handleInputChange}
                    disabled={!!editingProjectId} placeholder="PJ-001"
                    className="w-full bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50 disabled:text-slate-400 shadow-sm transition-all placeholder-slate-400"
                  />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Status</label>
                  <div className="relative">
                    <select
                        name="status" value={formData.status} onChange={handleInputChange}
                        className="w-full appearance-none bg-white ring-1 ring-slate-200 rounded-xl px-5 py-3.5 text-sm font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition-all cursor-pointer"
                    >
                        <option value="Active">🟢 Active</option>
                        <option value="Inactive">⚪ Inactive</option>
                        <option value="Completed">🔵 Completed</option>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              <div className='flex flex-col sm:flex-row justify-between gap-5'>
                  {/* Dedicated Auditor Ref */}
                  <div className="relative w-full" ref={auditorRef}>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Primary Auditor</label>
                      <div
                          onClick={() => setOpenDropdown(openDropdown === 'auditor' ? null : 'auditor')}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-white cursor-pointer flex justify-between items-center hover:border-blue-400 transition-colors"
                      >
                          <span className="text-sm font-medium text-slate-700 flex items-center truncate">
                              <ShieldCheck className="w-4 h-4 mr-2 text-blue-600" />
                              {/* {selectedAuditor ? selectedAuditor.name : "Select Auditor"} */}
                              {formData?.auditor ? formData?.auditor.name : "Select Auditor"}
                          </span>
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>
                      {openDropdown === 'auditor' && (
                          <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl py-2 max-h-48 overflow-y-auto">
                              {mockUsers?.map(user => (
                                  <div
                                      key={user.id}
                                      onClick={(e) => { 
                                        e.preventDefault(); 
                                        e.stopPropagation(); 
                                        setFormData(prev => ({
                                          ...prev,
                                          auditor: user
                                        }));
                                        setSelectedAuditor(user); 
                                        setOpenDropdown(null); 
                                      }}
                                      className="px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer flex justify-between items-center transition-colors"
                                  >
                                      {user.name}
                                      {formData.auditor?.id === user.id && <Check className="w-4 h-4 text-blue-600" />}
                                      {/* {selectedAuditor?.id === user.id && <Check className="w-4 h-4 text-blue-600" />} */}
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>

                  {/* Dedicated Coordinator Ref */}
                  <div className="relative w-full" ref={coordinatorRef}>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Coordinator</label>
                      <div
                          onClick={() => setOpenDropdown(openDropdown === 'coordinator' ? null : 'coordinator')}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-white cursor-pointer flex justify-between items-center hover:border-blue-400 transition-colors"
                      >
                          <span className="text-sm font-medium text-slate-700 flex items-center truncate">
                              <Briefcase className="w-4 h-4 mr-2 text-emerald-600" />
                              {/* {selectedCoordinator ? selectedCoordinator.name : "Select Coordinator"} */}
                              {formData?.coordinator ? formData.coordinator?.name : "Select Coordinator"}
                          </span>
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>
                      {openDropdown === 'coordinator' && (
                          <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl py-2 max-h-48 overflow-y-auto">
                              {mockUsers?.map(user => (
                                  <div
                                      key={user.id}
                                      onClick={(e) => { 
                                        e.preventDefault(); 
                                        e.stopPropagation();
                                        
                                        setFormData(prev => ({
                                          ...prev,
                                          coordinator: user
                                        }));

                                        setSelectedCoordinator(user); 
                                        setOpenDropdown(null); 
                                      }}
                                      className="px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-blue-50 hover:text-blue-700 cursor-pointer flex justify-between items-center transition-colors"
                                  >
                                      {user.name}
                                      {formData.coordinator?.id === user.id && <Check className="w-4 h-4 text-blue-600" />}
                                      {/* {selectedCoordinator?.id === user.id && <Check className="w-4 h-4 text-blue-600" />} */}
                                  </div>
                              ))}
                          </div>
                      )}
                  </div>
              </div>

              <div className='flex flex-col sm:flex-row justify-between gap-5'>
                  <div className="w-full">
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Due Date</label>
                      <input
                          type="date"
                          value={formData?.dueDate}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              dueDate: e.target.value
                            }))
                          }
                          // value={dueDate}
                          // onChange={(e) => setDueDate(e.target.value)}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-slate-700 bg-white"
                      />
                  </div>

                  {/* Dedicated Language Ref */}
                  <div className="relative w-full" ref={languageRef}>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Select Languages</label>
                      <div
                          onClick={() => {
                              setOpenDropdown(openDropdown === 'language' ? null : 'language');
                              setTempSelectedLanguages(formData?.languages); // ✅ use formData
                              // setTempSelectedLanguages(selectedLanguages);
                          }}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-white cursor-pointer flex justify-between items-center hover:border-blue-400 transition-colors"
                      >
                          <span className="text-sm font-medium text-slate-700 truncate">
                            { formData.languages.length > 0
                                ? `${formData.languages.length} Selected`
                                : "Select..."
                            }
                              {/* {selectedLanguages.length > 0 ? `${selectedLanguages.length} Selected` : "Select..."} */}
                          </span>
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>

                      {openDropdown === 'language' && (
                          <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                              <div className="max-h-48 overflow-y-auto p-3 space-y-1">
                                  {availableLanguages.map(lang => (
                                      <div
                                          key={lang.id}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setTempSelectedLanguages(prev =>
                                                prev.includes(lang.id) ? prev.filter(id => id !== lang.id) : [...prev, lang.id]
                                              )
                                          }}
                                          className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-slate-200"
                                      >
                                          <span className="text-sm font-bold text-slate-800">{lang.name}</span>
                                          <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors border ${tempSelectedLanguages.includes(lang.id) ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
                                              {tempSelectedLanguages.includes(lang.id) && <Check className="w-3.5 h-3.5 text-white stroke-[3]" />}
                                          </div>
                                      </div>
                                  ))}
                              </div>
                              <div className="p-3 border-t border-slate-100 bg-slate-50 flex justify-end">
                                  <button
                                      type="button"
                                      onClick={handleApplyLanguages}
                                      className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700"
                                  >
                                      Apply
                                  </button>
                              </div>
                          </div>
                      )}
                  </div>
              </div>

              <div className='flex flex-col sm:flex-row justify-between gap-5'>
                  {/* Dedicated Framework Ref */}
                  <div className="relative w-full" ref={frameworkRef}>
                      <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Select Frameworks</label>
                      <div
                          onClick={() => {
                              setOpenDropdown(openDropdown === 'framework' ? null : 'framework');
                              setTempSelectedFrameworks(formData?.frameworks); // ✅ use formData
                              // setTempSelectedFrameworks(selectedFrameworks);
                          }}
                          className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-white cursor-pointer flex justify-between items-center hover:border-blue-400 transition-colors"
                      >
                          <span className="text-sm font-medium text-slate-700 truncate">
                              { formData.frameworks.length > 0
                                  ? `${formData.frameworks.length} Selected`
                                  : "Select..."
                              }
                              {/* {selectedFrameworks.length > 0 ? `${selectedFrameworks.length} Selected` : "Select..."} */}
                          </span>
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                      </div>

                      {openDropdown === 'framework' && (
                          <div className="absolute z-20 mt-2 w-full bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
                              <div className="max-h-48 overflow-y-auto p-3 space-y-1">
                                  {frameworks.map(fw => (
                                      <div
                                          key={fw.frameworkId}
                                          onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              setTempSelectedFrameworks(prev =>
                                                prev.includes(fw.frameworkId) ? prev.filter(id => id !== fw.frameworkId) : [...prev, fw.frameworkId]
                                              )
                                          }}
                                          className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors border border-transparent hover:border-slate-200"
                                      >
                                          <span className="text-sm font-bold text-slate-800">{fw.name}</span>
                                          <div className={`w-5 h-5 rounded flex items-center justify-center transition-colors border ${tempSelectedFrameworks.includes(fw.frameworkId) ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`}>
                                              {tempSelectedFrameworks.includes(fw.frameworkId) && <Check className="w-3.5 h-3.5 text-white stroke-[3]" />}
                                          </div>
                                      </div>
                                  ))}
                              </div>
                              <div className="p-3 border-t border-slate-100 bg-slate-50 flex justify-end">
                                  <button
                                      type="button"
                                      onClick={handleApplyFrameworks}
                                      className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg shadow-sm hover:bg-blue-700"
                                  >
                                      Apply
                                  </button>
                              </div>
                          </div>
                      )}
                  </div>
              </div>

              {/* Dedicated Team Ref */}
              <div ref={teamDropdownRef} className="relative pt-2">
                <label className="block text-xs font-black text-slate-500 uppercase tracking-wider mb-2">Assign Team Members <span className="text-rose-500">*</span></label>
                <div
                  onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                  className={`min-h-[52px] bg-white ring-1 rounded-xl p-2 flex flex-wrap gap-2 items-center cursor-pointer shadow-sm transition-all
                    ${isUserDropdownOpen ? 'ring-2 ring-indigo-500' : 'ring-slate-200 hover:ring-slate-300'}`}
                >
                  {formData.assignedUsers.map(user => (
                    <span key={user} className="flex items-center gap-2 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200/50 pl-2.5 pr-1.5 py-1.5 rounded-lg text-xs font-bold">
                      {user}
                      <button type="button" onClick={(e) => { e.stopPropagation(); handleUserToggle(user); }} className="text-indigo-400 hover:text-rose-500 hover:bg-white rounded p-0.5 transition-colors">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                  {formData.assignedUsers.length === 0 && <span className="text-slate-400 text-sm font-medium ml-3">Select team members...</span>}
                  <ChevronDown className={`w-4 h-4 text-slate-400 ml-auto mr-3 transition-transform ${isUserDropdownOpen ? 'rotate-180' : ''}`} />
                </div>

                {isUserDropdownOpen && (
                  <div className="absolute top-full left-0 w-full mt-2 bg-white ring-1 ring-slate-200 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-50 max-h-60 overflow-y-auto py-2 animate-in fade-in slide-in-from-top-2 custom-scrollbar">
                    {AVAILABLE_USERS.map(user => {
                      const isSelected = formData.assignedUsers.includes(user);
                      return (
                        <div 
                            key={user} 
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleUserToggle(user);
                            }} 
                            className={`flex items-center justify-between px-4 py-3 mx-2 rounded-xl cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/80' : 'hover:bg-slate-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[10px] font-black ring-1 ${isSelected ? 'bg-indigo-600 text-white ring-indigo-600' : 'bg-slate-100 text-slate-600 ring-slate-200'}`}>
                                {getInitials(user)}
                            </div>
                            <span className={`text-sm ${isSelected ? 'font-black text-indigo-900' : 'font-semibold text-slate-700'}`}>{user}</span>
                          </div>
                          {isSelected && <Check className="w-5 h-5 text-indigo-600" />}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="px-8 py-6 border-t border-slate-100 flex justify-end gap-3 bg-white rounded-b-[2rem] z-20">
              <button onClick={closeModal} className="px-6 py-3 text-sm font-bold text-slate-600 bg-white ring-1 ring-slate-200 rounded-xl hover:bg-slate-50 transition-all shadow-sm">
                Cancel
              </button>
              <button onClick={handleSaveProject} className="px-8 py-3 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 transition-all shadow-md hover:shadow-lg active:scale-95">
                {editingProjectId ? 'Save Changes' : 'Create Project'}
              </button>
            </div>
          </div>
        </div>
      )}

      </div>
  );
};

export default Projects;